import { LiveWindowLabel } from '../LivePreview/liveViewModel';

/*
 * ONE renderer for the window label, in TWO widths — because the two widths appear on the same
 * screen, over the same counts, and must never be able to name different periods.
 *
 * The RESULTS heading has a line to itself and keeps the label in full, to the minute:
 * "since enabled Sep 14, 8:00 AM EDT". Those minutes are load-bearing there — see
 * liveWireAdapter.windowLabelFor — because a pilot armed at 08:00 rendered as a bare day is
 * handed the eight hours before it was on, credited to it by the heading above its own counts.
 *
 * The four HEADLINE TILES share one 4-column grid (FirstResponder.module.css .stats), and that
 * same string wrapped each caption to two and three lines: the captions grew taller than the
 * numbers they label, the four values stopped sitting on one baseline, and the stat row pushed
 * the card open. So the tiles carry the DATE HEAD of the label instead.
 *
 * The guarantee is structural, not editorial: `short` returns a literal PREFIX of the full label
 * (a slice of it), never a second rendering of the instant. There is no second formatter and no
 * second instant for the tile to drift from — the caption is the heading, truncated. Anything
 * that broke that (a re-format on a different clock, a different source field) stops being a
 * prefix and fails the test on the spot. The detail the tile drops is not lost: HeadlineStats
 * hangs the full label in the tile's `title`.
 *
 * The labels that name no period at all ('now' for a live cohort, 'unresolved' for a backlog,
 * and the honest 'window unknown') have no date head to take and pass through unchanged in both
 * widths — they were never the strings that wrapped.
 */

/*
 * The date head formatVenueTime writes for BOTH branches of windowLabelFor: venueTime's own
 * 'date' pattern ('MMM d'), preceded by the word that says which instant it is. Everything after
 * it is either the enable instant's time of day or the zone abbreviation — the detail the
 * heading keeps and the tile has no room for.
 *
 * A label with no date head takes no truncation. That covers the period-less captions and, just
 * as deliberately, "since enabled Unknown": an instant the wire did not substantiate renders as
 * the same word the metric cells use, and the tile repeats it rather than inventing a day.
 */
const DATE_HEAD = /^since (?:enabled )?[A-Z][a-z]{2} \d{1,2}/;

export const windowCaption = (
  label: LiveWindowLabel,
  short = false,
): string => {
  if (!short) return label;
  const head = DATE_HEAD.exec(label);
  return head === null ? label : head[0];
};
